import React, { useEffect, useMemo, useState } from 'react';
import { Form, Radio, Select } from 'antd';
import {
  getAdminAllMember,
  getAdminMessageById,
  postAdminMessage,
} from '../../../api';
import {
  MessageType,
  ISystemMessageReq,
  CodeMessageCenter,
  CommonStatusStep,
  IMessageResItem,
} from '../../../model/api-modules';
import { splitEmail } from '../../utils';
import { SYSTEM_LIST } from '../config';
import { getStudioOptions } from '../../utils/admin';
import { LabeledValue } from 'antd/lib/select';
import 'vditor/dist/index.css';
import Vditor from 'vditor';
import '../style.vditor.scss';
import { ValidateStatus } from 'antd/es/form/FormItem';
import classNames from 'classnames';
import { messageError, messageSuccess } from '../../../components/message';
import getFormatMessage from '../../../locales/get-format-message';
import { ConsoleDrawer } from '../../../components/drawer';
import { ConsoleForm } from '../../../components/form';
import { CommonSelect } from '../../../components/select';
import { CommonInput } from '../../../components/input';

interface IProps {
  record?: IMessageResItem;
  site?: string;
  viewOnly?: boolean;
  onClose?: () => void;
  onSave: () => void;
}

export const AdminMessageDrawer = (props: IProps) => {
  const { record, viewOnly, site, onClose, onSave } = props;
  const [viewInfo, setViewInfo] = useState<IMessageResItem>();
  const [studioList, setStudioList] = useState<LabeledValue[]>([]);
  const [memberList, setMemberList] = useState<LabeledValue[]>([]);
  const [loading, setLoading] = useState<boolean>(false);

  const [vd, setVd] = React.useState<Vditor>();
  const [contentValidateStatus, setContentValidateStatus] =
    useState<ValidateStatus>('');
  const [contentOverFlow, setContentOverFlow] = useState<boolean>(false);

  const MAX_LENGTH = 2000;
  const [form] = Form.useForm();

  useEffect(() => {
    init();
  }, [record]);
  const init = async () => {
    if (!record?.id) {
      return;
    }
    const res = await getAdminMessageById(record?.id);
    setViewInfo(res);
  };

  const sendMessage = async (value: ISystemMessageReq) => {
    const res = await postAdminMessage(value);
    if (res?.code === 0) {
      messageSuccess(getFormatMessage('successfully'));
    } else if (res?.code === CodeMessageCenter.CreateFail) {
      messageError(res.msg);
    } else {
      messageError(getFormatMessage('failed'));
    }
  };

  const onVdInput = (value: string) => {
    const status = value.trim().length ? '' : 'error';
    setContentValidateStatus(status);
    setContentOverFlow(value.trim().length > MAX_LENGTH);
  };

  const onFinish = async () => {
    const values = await form.validateFields();
    const studioID = values.StudioID;
    const receiver = values.Receiver;
    if (studioID && !receiver) {
      // 如果选择了工作室,需要选择接收人
      form.setFields([
        {
          name: 'Receiver',
          errors: [getFormatMessage('please_select_receiver')],
        },
      ]);
      return false;
    }
    const content = vd?.getHTML() || '';
    if (!content.trim().length) {
      setContentValidateStatus('error');
      setContentOverFlow(false);
      return false;
    }
    if (content.trim().length > MAX_LENGTH) {
      setContentValidateStatus('error');
      setContentOverFlow(true);
      return false;
    }
    setContentValidateStatus('');
    setContentOverFlow(false);

    setLoading(true);
    try {
      await sendMessage({
        ...values,
        Type: MessageType.System,
        NeedUpdated:
          typeof values.NeedUpdated === 'number'
            ? values.NeedUpdated
            : CommonStatusStep.Pending,
        Content: content,
      });
      // 提交成功重置
      form.resetFields();
      onSave();
      return true;
    } catch (error) {
      console.error(error);
    }
    setLoading(false);
    return false;
  };

  const handleChange = (value: string) => {
    getMember(value);
  };
  const typeChange = (_value: string) => {
    // console.log(`selected ${value}`);
  };
  const handleMemberChange = (_value: string) => {
    // console.log(`selected ${value}`);
  };

  const getMember = async (studioId: string) => {
    if (!studioId) return;
    const memberRes = await getAdminAllMember(studioId);
    if (memberRes) {
      const list: LabeledValue[] = [];
      memberRes.forEach((item) => {
        list.push({
          value: item.UserID,
          label: item.UserName || splitEmail(item.UserID),
        });
      });
      if (list.length) {
        setMemberList(list);
      }
    }
  };

  useEffect(() => {
    getStudioOptions(site).then((list) => setStudioList(list));
    initVditor();
  }, [loading]);

  const initVditor = () => {
    if (viewOnly) {
      return;
    }
    const vditor = new Vditor(`vditor-${site}`, {
      height: 200,
      lang: 'en_US',
      placeholder: getFormatMessage('please_update_content'),
      toolbar: [
        'headings',
        'bold',
        'italic',
        'strike',
        'link',
        '|',
        'list',
        'ordered-list',
        'check',
        'outdent',
        'indent',
        '|',
        'quote',
        'table',
        '|',
        'undo',
        'redo',
        '|',
        'edit-mode',
      ],
      mode: 'wysiwyg',
      input: onVdInput,
      after: () => {
        vditor.setValue(getFormatMessage('content_example'));
        setVd(vditor);
      },
    });
  };

  const errorTips = useMemo(() => {
    if (contentOverFlow) {
      return getFormatMessage('exceed_character_limit', {
        params: {
          maxLength: MAX_LENGTH,
        },
      });
    }

    if (contentValidateStatus) {
      return getFormatMessage('please_update_content');
    }

    return '';
  }, [contentOverFlow, contentValidateStatus]);

  if (viewOnly) {
    return (
      <ConsoleDrawer title="查看推送详情" onClose={onClose} footer={<></>}>
        <ConsoleForm className="admin-form" form={form}>
          <Form.Item label={getFormatMessage('system_type')} name="SubType">
            {
              SYSTEM_LIST.find((item) => item.value === viewInfo?.SubType)
                ?.label
            }
          </Form.Item>
          <Form.Item label={getFormatMessage('title')} name="Title">
            {viewInfo?.Title}
          </Form.Item>
          <Form.Item label={getFormatMessage('content')} name="Content">
            <div
              dangerouslySetInnerHTML={{ __html: viewInfo?.Content || '' }}
            ></div>
          </Form.Item>
          <Form.Item label={getFormatMessage('need_update')} name="NeedUpdated">
            {getFormatMessage(viewInfo?.NeedUpdated ? 'yes' : 'no')}
          </Form.Item>
          <Form.Item
            label={getFormatMessage('studio_name_uppercase')}
            name="StudioID"
          >
            {studioList.find((item) => item.value === viewInfo?.StudioID)
              ?.label || ''}
          </Form.Item>
          <Form.Item label={getFormatMessage('receiver')} name="Receiver">
            {viewInfo?.Receiver || ''}
          </Form.Item>
        </ConsoleForm>
      </ConsoleDrawer>
    );
  }

  return (
    <ConsoleDrawer title="新建消息推送" onClose={onClose} onOk={onFinish}>
      <ConsoleForm className="admin-form" form={form}>
        <Form.Item
          label={getFormatMessage('system_type')}
          name="SubType"
          rules={[
            {
              required: !viewOnly,
              message: getFormatMessage('please_select_type'),
            },
          ]}
        >
          <CommonSelect
            placeholder={getFormatMessage('please_select_system_type')}
            style={{ width: 300 }}
            onChange={typeChange}
            options={SYSTEM_LIST}
          />
        </Form.Item>
        <Form.Item
          label={getFormatMessage('标题')}
          name="Title"
          rules={[{ required: !viewOnly, message: '请输入消息标题' }]}
        >
          <CommonInput style={{ width: 300 }} placeholder="请输入消息标题" />
        </Form.Item>
        <Form.Item
          label={getFormatMessage('content')}
          name="Content"
          rules={[
            {
              required: !viewOnly,
              message: '',
              validator: () => Promise.resolve(true),
            },
          ]}
        >
          <div
            id={`vditor-${site}`}
            className={classNames('vditor', errorTips ? 'error' : '')}
          />
          {errorTips ? (
            <div
              id="basic_Content_help"
              className="ant-form-item-explain ant-form-item-explain-connected"
              role="alert"
            >
              <div className="ant-form-item-explain-error">{errorTips}</div>
            </div>
          ) : null}
        </Form.Item>
        <Form.Item label={getFormatMessage('need_update')} name="NeedUpdated">
          <Radio.Group>
            <Radio value={CommonStatusStep.Resolve}>
              {getFormatMessage('yes')}
            </Radio>
            <Radio value={CommonStatusStep.Pending}>
              {getFormatMessage('no')}
            </Radio>
          </Radio.Group>
        </Form.Item>
        <Form.Item
          label={getFormatMessage('studio_name_uppercase')}
          name="StudioID"
        >
          <Select
            allowClear
            placeholder={getFormatMessage('studio_warn_select')}
            style={{ width: 300 }}
            onChange={handleChange}
            options={studioList}
            showSearch={true}
          />
        </Form.Item>
        <Form.Item label={getFormatMessage('receiver')} name="Receiver">
          <Select
            allowClear
            showSearch
            mode="multiple"
            placeholder={getFormatMessage('default_all')}
            style={{ width: 300 }}
            onChange={handleMemberChange}
            options={memberList}
          />
        </Form.Item>
      </ConsoleForm>
    </ConsoleDrawer>
  );
};
